import { Hit, WorkspaceResource, WorkspaceResponse, User } from 'polly-commons';
import { ESWorkspaceAttributes, ESWorkspacePermission, WorkspaceFilters, WorkspacePermissions } from '../../interfaces';
import { API_QUERY_PARAMS, WorkspaceType } from '../../../polly-manage.constants';
import bodybuilder from 'bodybuilder';
import { ES_WORKSPACES_INDICES } from '../../../modules/workspace-module/polly-manage-workspace.constants';

/**
 * @note ES stands for ElasticSearch, which we have integrated in workspaces for listing and searching
 *       workspaces and contents within those
 */

/**
 * @description Converts the v2 create workspace API response to ESResponse for populating on UI side
 *
 * @param workspace Object in form of API Response
 * @param creator Current user, because current user will only be the creator of workspace
 * @return ESWorkspaceAttributes - converted ES Response object
 */
export function convertWorkspaceResponseToESResponse(workspace: WorkspaceResponse, creator: User | null): ESWorkspaceAttributes {
  let permissions: ESWorkspacePermission[] = [];
  if (!!creator) {
    permissions = [
      {
        access: 'manage',
        updated_access: 'manage',
        is_bookmarked: workspace.data.attributes.favourite,
        watch: workspace.data.attributes.watch,
        user_id: Number(creator.id),
        user_email: (creator && creator.attributes.email) || '',
        user_name: (creator && `${creator.attributes.first_name} ${creator.attributes.last_name}`) || '',
      },
    ];
  }
  return {
    id: workspace.data.attributes.id,
    created_by: (creator && `${creator.attributes.first_name} ${creator.attributes.last_name}`) || '',
    creator_email: (creator && creator.attributes.email) || '',
    description: workspace.data.attributes.description,
    created_date: new Date(workspace.data.attributes.created_time).getTime(),
    creator_id: workspace.data.attributes.creator.toString(),
    modified_by: workspace.data.attributes.creator.toString(),
    modified_date: new Date(workspace.data.attributes.last_modified).getTime(),
    name: workspace.data.attributes.name,
    organization_id: workspace.data.attributes.organisation,
    permissions,
    status: workspace.data.attributes.status,
    tags: workspace.data.attributes.tag_names.map((tag) => ({ tag_name: tag })),
  };
}

/**
 * @description Used to get the object to be stored within the workspaces array (Hits is an array of all the workspaces objects)
 *
 * @param workspace object to be wrapped
 * @return Hit<ESWorkspaceAttributes>
 */
export function getInitialESResponseObject(workspace: ESWorkspaceAttributes): Hit<ESWorkspaceAttributes> {
  return {
    _id: workspace.id.toString(),
    _index: ES_WORKSPACES_INDICES.WORKSPACE_INDEX,
    _score: 0,
    _source: workspace,
    _type: '_doc',
    sort: [],
  };
}

/**
 * @description Converts the ES Workspace Object to v2 interface used for storing workspace details.
 * @reason The v2 interface for workspace is being passed at many places for UI side
 *
 * @param workspace ES Workspace object
 * @param currentUserId user_id of current user
 * @return WorkspaceResource
 */
export function convertESResponseToWorkspaceResponse(workspace: ESWorkspaceAttributes, currentUserId: number): WorkspaceResource {
  let favourite: boolean;
  let watch: boolean;
  try {
    favourite = workspace.permissions.filter((userPermission) => userPermission.user_id === currentUserId)[0].is_bookmarked;
    watch = workspace.permissions.filter((userPermission) => userPermission.user_id === currentUserId)[0].watch;
  } catch (_) {
    favourite = watch = false;
  }
  return {
    id: workspace.id.toString(),
    type: 'workspaces',
    attributes: {
      active: true,
      created_time: new Date(workspace.created_date),
      creator: +workspace.creator_id,
      description: workspace.description,
      favourite,
      watch,
      id: workspace.id,
      last_modified: new Date(workspace.modified_date),
      name: workspace.name,
      organisation: workspace.organization_id,
      status: workspace.status,
      tag_names: workspace.tags.map((tag) => tag.tag_name),
      project_property: {
        labels: [],
        type: '',
      },
    },
  };
}

/**
 * @description Builds ES Query for fetching the workspace list
 *
 * @param from Pagination number
 * @param filters Workspace filters if applied, default value: {showAll: true}
 * @param pageSize pagination applied for search results
 * @param hideReadAccessWorkspaces Whether to hide workspaces with readonly access, used for copy/move operations on workspaces
 * @param [status] optional parameter to get active/archived workspaces
 * @return object ES Query
 */
export function buildWorkspaceListingQuery(
  from: number,
  { searchString, includeTags = false }: { searchString?: string; includeTags?: boolean } = {},
  filters: WorkspaceFilters = { showAll: true },
  pageSize: number = API_QUERY_PARAMS.WORKSPACE_PAGE_SIZE_VALUE,
  hideReadAccessWorkspaces: boolean = false
): object {
  const queryBuilder = bodybuilder();
  queryBuilder.from(from).size(pageSize);
  if (filters.showOwned) queryBuilder.andQuery('term', 'creator_id', '{user_id}');
  queryBuilder.andQuery('nested', { path: 'permissions' }, (q: bodybuilder.Bodybuilder) => {
    if (filters.showFavourites) q.query('match', 'permissions.is_bookmarked', true);
    if (hideReadAccessWorkspaces) q.notQuery('match', 'permissions.access', 'read');
    return q.andQuery('match', 'permissions.user_id', '{user_id}');
  });
  if (filters.showShared) queryBuilder.notQuery('term', 'creator_id', '{user_id}');
  if (filters.showArchived) queryBuilder.andQuery('term', 'status', WorkspaceType.ARCHIVED_WORKSPACE_VALUE);
  else if (filters.showActive) queryBuilder.andQuery('term', 'status', WorkspaceType.ACTIVE_WORKSPACE_VALUE);
  if (!!searchString) {
    const formattedSearchString = getFormattedSearchString(searchString);
    const searchStringQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
      builder.orQuery('query_string', { default_field: 'name', query: `*${formattedSearchString}*` });
      if (includeTags) {
        builder.orQuery('nested', { path: 'tags' }, (qBuilder: bodybuilder.Bodybuilder) => {
          return qBuilder.query('query_string', { default_field: 'tags.tag_name', query: `*${formattedSearchString}*` });
        });
      }
      return builder;
    };
    queryBuilder.andQuery('bool', searchStringQuery);
  }
  queryBuilder.sort([{ created_date: 'desc' }]);
  return queryBuilder.build();
}

/**
 * @description Builds ES Query for fetching specific workspace details
 *
 * @param workspaceId ID of the workspace
 * @return object ES Query
 */
export function buildWorkspaceDetailsQuery(workspaceId: number): object {
  const queryBuilder = bodybuilder();
  queryBuilder.andQuery('term', 'id', workspaceId).andQuery('nested', { path: 'permissions' }, (q: bodybuilder.Bodybuilder) => {
    return q.query('match', 'permissions.user_id', '{user_id}');
  });
  return queryBuilder.build();
}

/**
 * @description Returns the formatted search string, which will be used by elasticsearch query to return proper results.
 * It performs following things:
 * - Adds `*` before symbol -> tilde (~)
 * - Adds `AND` keyword between all the space separated words
 * - Adds `\\` before the symbols -> ;,:,(,),{,},[,],\, !, ^
 * - Adds `\\` after symbol -> *
 *
 * @param searchString search string which needs to be updated
 * @return string
 */
export function getFormattedSearchString(searchString: string): string {
  if (searchString[0] === '~') searchString = searchString.replace(/(~)/, '*$1');

  const words: string[] = searchString
    .trim()
    .toLowerCase()
    .split(/[\s+]/)
    .filter((word) => word !== '');
  searchString = words
    .join(' AND ')
    .replace(/([:;(){}/!^\\[\]])/g, '\\$1')
    .replace(/(\*)/g, '$1\\');

  return searchString;
}

/**
 * @description This function will be used by components using highlight mechanism for searching - Workspace card view and content list.
 * - It escapes the special characters used for regex purpose from the search string to get proper highlighted string
 *
 * @param searchString from which, we need to escape special characters.
 * @return string
 */
export function escapeRegexSpecialChars(searchString: string): string {
  return searchString.replace(/([.*+?^$|(){}\\[\]])/gm, '\\$1');
}

/**
 * @description Get the generic search info points for workspace and content search
 *
 * @return string
 */
function getGenericSearchInfo(): string {
  return `<li><p> A maximum of 30 characters can be searched. </p></li>
          <li><p> Search results are retrieved based on partial matching. <br>
            Eg. If <mark>test</mark>is entered as the search input, <br> \
            then all the results with the word <mark>test</mark>, <mark>testing</mark> etc. will be retrieved.
          </p></li>`;
  }

/**
 * @description Get the info config for workspaces search containing the list of information and styles for tooltip
 *
 * @return string
 */
export function getWorkspaceSearchInfoTooltipConfig(): string {
  return  `<ul class='search-info-list'> <li> <p> Minimum 3 characters should be entered to search. </p></li>
              ${getGenericSearchInfo()}
              <li><p> In case of two or more search terms entered, all the terms are searched in the workspace name. </p></li>
            </ul>`;
}

/**
 * @description Get the info config for content search containing the list of information and styles for tooltip
 *
 * @return string
 */
export function getContentSearchInfoTooltipConfig(): string {
  return `<ul class='search-info-list'> ${getGenericSearchInfo()}
            <li><p> In case of two or more search terms entered, all the terms are searched in the workspace contents. </p></li>
            <li><p> In case of folders, search is performed only at the parent folder level.<br> \
                  To search within a folder, user needs to navigate inside a folder and then search. </p></li>
          </ul>`;
}

/**
 * @description Builds ES Query for fetching tags suggestions for an organization
 *
 * @param tagSearchString Search query for tags
 */
export function buildTagSuggestionQuery(tagSearchString: string): object {
  const queryBuilder: bodybuilder.Bodybuilder = bodybuilder();
  queryBuilder.andQuery('match', 'organization_id', '{organization_id}');
  const tagSearchQuery = (builder: bodybuilder.Bodybuilder): bodybuilder.Bodybuilder => {
    builder.orQuery('query_string', { default_field: 'tag_name', query: `*${tagSearchString}*` });
    return builder;
  }
  queryBuilder.andQuery('bool', tagSearchQuery);
  return queryBuilder.build();
}

/**
 * @param newCollaborators Newly assigned collaborators list
 */
export function processNewCollaboratorsResponse(newCollaborators: WorkspacePermissions): ESWorkspacePermission[] {
  return newCollaborators.data.map((collaborator) => ({
      access: collaborator.attributes.permission,
      user_id: collaborator.attributes.user_id,
      user_email: collaborator.attributes.user_email,
      user_name: collaborator.attributes.user_name,
      watch: false,
      updated_access: collaborator.attributes.permission,
      is_bookmarked: false,
    }));
}
